import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';

import { BodyText, Icon, IconName, theme } from '@sg/garnish';

export const SavedPaymentMethod = (props: SavedPaymentMethodProps) => {
  const {
    icon,
    description,
    a11yLabel,
    style,
    isDefault,
    isExpiring,
    isExpired,
    isSuccess,
    onPress,
  } = props;

  const { formatMessage } = useIntl();
  const containerStyle = [styles.container, style];

  return (
    <TouchableOpacity
      style={containerStyle}
      accessibilityLabel={a11yLabel ?? formatMessage(messages.manage)}
      onPress={onPress}
    >
      <View style={styles.content}>
        <Icon name={icon} width={24} height={24} />

        <BodyText sizeMatch={['14']}>{description}</BodyText>
      </View>

      <View style={styles.state}>
        {isDefault && !isExpired && !isExpiring ? (
          <BodyText sizeMatch={['14']}>
            <FormattedMessage {...messages.default} />
          </BodyText>
        ) : null}

        {isExpired && !isExpiring ? (
          <BodyText sizeMatch={['14']} style={styles.warningText}>
            <FormattedMessage {...messages.expired} />
          </BodyText>
        ) : null}

        {isExpiring && !isExpired ? (
          <BodyText sizeMatch={['14']} style={styles.warningText}>
            <FormattedMessage {...messages.expiring} />
          </BodyText>
        ) : null}

        {isSuccess ? (
          <Icon name="IconBadgeCheck" color={theme.colors.SPINACH} width={16} height={16} />
        ) : (
          <Icon name="IconCaretRight" width={16} height={16} />
        )}
      </View>
    </TouchableOpacity>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  manage: {
    defaultMessage: 'Manage payment methods',
    description: 'Saved Payment Method | Manage',
  },
  default: {
    defaultMessage: 'Default',
    description: 'Saved Payment Method | Default',
  },
  expired: {
    defaultMessage: 'Expired',
    description: 'Saved Payment Method | Expired',
  },
  expiring: {
    defaultMessage: 'Expiring',
    description: 'Saved Payment Method | Expiring',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['6'],
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  state: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  warningText: {
    color: theme.colors.CAUTION,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SavedPaymentMethodProps = {
  icon: IconName;
  description: string;
  a11yLabel?: string;
  style?: StyleProp<ViewStyle>;
  isDefault?: boolean;
  isExpiring?: boolean;
  isExpired?: boolean;
  isSuccess?: boolean;
  onPress: () => void;
};
